/**
 * A link that needs a login, such as one in an email, opens after the login. The path is kept in
 * the tab while the user logs in, also through SSO, and only a path in the app is followed.
 */
const KEY = 'trustpulse.returnPath';

/** Older than this, the user has moved on */
const MAX_AGE_MS = 30 * 60 * 1000;

/** A path in this app: never another site, the login page or the API */
export const safeReturnPath = (path: unknown): string | null => {
  if (typeof path !== 'string' || path.length > 2000) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f]/.test(path) || path.includes('://')) return null;
  const pathname = path.split(/[?#]/)[0];
  if (pathname === '/' || pathname.startsWith('/api/') || pathname === '/api') return null;
  return path;
};

export const rememberReturnPath = (path: string, now = Date.now()): void => {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path: safe, at: now }));
  } catch {
    // Without storage, the user lands on their start page as before
  }
};

/** The path to go back to, once. It is removed when read. */
export const takeReturnPath = (now = Date.now()): string | null => {
  try {
    const stored = sessionStorage.getItem(KEY);
    if (!stored) return null;
    sessionStorage.removeItem(KEY);
    const { path, at } = JSON.parse(stored) as { path?: unknown; at?: unknown };
    if (typeof at !== 'number' || now - at > MAX_AGE_MS) return null;
    return safeReturnPath(path);
  } catch {
    return null;
  }
};
